import * as React from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  Ban,
  CalendarDays,
  CalendarRange,
  Check,
  CircleSlash,
  Hand,
  MapPin,
  Route as RouteIcon,
  ScanSearch,
  TriangleAlert,
  UserPlus,
  UserX,
} from "lucide-react";
import {
  SEVERITY_META,
  SEVERITY_TIERS,
  visitTypeColor,
  type AppointmentWithContext,
  type AssessmentFeedItem,
  type SiteInsightsResponse,
  type SiteSummaryResponse,
} from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/Card";
import { ErrorState } from "@/components/ui/Feedback";
import { IconTile } from "@/components/ui/IconTile";
import { PageHeader } from "@/components/ui/PageHeader";
import { Stat } from "@/components/ui/Stat";
import { Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { TextLink } from "@/components/ui/TextLink";
import { AgendaList } from "@/components/schedule/AgendaList";
import { AppointmentPopover } from "@/components/schedule/AppointmentPopover";
import { ColumnChart } from "@/components/charts/ColumnChart";
import { MeterStat } from "@/components/charts/MeterStat";
import { DonutChart } from "@/components/charts/DonutChart";
import { MiniBar } from "@/components/charts/RankedBars";
import { TrendChart } from "@/components/charts/TrendChart";
import { activityFrom, type Activity, type ActivityKind } from "@/lib/activity";
import { useAuth } from "@/lib/auth-context";
import {
  instantFrom,
  todayIn,
  addDays,
  formatDayAxisIn,
  formatRelative,
  zoneOffset,
} from "@/lib/dates";
import type { Point } from "@/lib/schedule";
import { useCompanySites } from "@/lib/scope-lists";
import { useTenancy } from "@/lib/tenancy";
import { useApi } from "@/lib/useApi";

/**
 * The console's landing page.
 *
 * The counts are real - one paginated request each, asking for a single row and
 * reading `pagination.total`, which PostgREST computes in the same statement as
 * the select.
 *
 * Everything under "Compliance & performance" is one request to
 * `GET /sites/:id/insights`. It used to be a seeded random-number generator in
 * `lib/demo/`, marked as sample data on screen; that file and its Dart twin are
 * gone, and `insights.controller.ts` holds the arithmetic that replaced them.
 *
 * ── What used to be here ────────────────────────────────────────────────────
 *
 * A "Your access" card: the role's description, a sentence about what the reader
 * could see, and every capability as a chip. It was answering "why can't I see
 * X" on a screen nobody opens with that question - they open it to see whether
 * anything needs their attention.
 *
 * Then two permanent placeholder tiles, Assessments and Open stop-works, both
 * reading "-" because neither feature exists yet. A dashboard whose first fold
 * is a third empty is worse than a shorter dashboard: it teaches the reader that
 * the numbers here are decorative. They come back when they can carry a figure.
 *
 * Team went for a different reason - it is a count that does not change from one
 * week to the next, so it was taking a tile from something that does.
 */

interface AgendaResponse {
  appointments: AppointmentWithContext[];
}

export function Overview() {
  const { user, can } = useAuth();
  const tenancy = useTenancy();
  const { activeSiteId, activeCompanyId } = tenancy;

  // The same panel the schedule opens, hung off the row that was clicked.
  // Opening an appointment from here used to navigate to the schedule and leave
  // the reader to find the row again, which is a page load to answer "what is
  // this one" - the question the click already contained.
  const [dialog, setDialog] = React.useState<{
    open: boolean;
    appointment: AppointmentWithContext | null;
    anchor: HTMLElement | null;
    point: Point | null;
  }>({ open: false, appointment: null, anchor: null, point: null });

  // Only the flag is cleared. The panel fades rather than vanishing - see
  // `CalendarPopover` - and dropping the appointment here would swap the record
  // being read for an empty "New appointment" form for the length of the fade.
  const closeDialog = () => setDialog((current) => ({ ...current, open: false }));

  // The list the scope switcher in the topbar is already holding - same key, so
  // this is a read of a response in hand rather than a second request. Not a
  // tile itself (a site count barely moves week to week) but read for
  // `showNextSteps` below.
  const sites = useCompanySites(activeCompanyId);

  // Bounded in the SITE's zone, not the browser's. "Today" at a Texas yard is
  // not the same twenty-four hours as today in Hyderabad, and a tile that
  // counted the reader's day would disagree with the schedule directly below it.
  const timezone = tenancy.homeSite?.timezone ?? "UTC";
  const zoneLabel = zoneOffset(timezone);
  const today = todayIn(timezone);
  const dayStart = instantFrom(today, 0, 0, timezone);
  const dayEnd = instantFrom(addDays(today, 1), 0, 0, timezone);

  /**
   * Every tile above, in one request.
   *
   * It was five - two on `/appointments`, two on `/visits`, one on `/users` -
   * each asking for `pageSize=1` and reading `pagination.total` off a row it
   * threw away. Five moments for the row to settle is a row that visibly
   * twitches, and the windows they counted over lived here and in Dart with
   * nothing keeping them equal. Only the site's own day boundary is ours to
   * send; the week back and the fortnight forward are the API's - see
   * `summary.controller.ts`.
   */
  const summary = useApi<SiteSummaryResponse>(
    can("appointment.read") && activeSiteId
      ? `/sites/${activeSiteId}/summary?todayStart=${encodeURIComponent(
          dayStart
        )}&tomorrowStart=${encodeURIComponent(dayEnd)}`
      : null
  );

  // A null is "you may not read this", not zero - so a figure the caller cannot
  // see falls through to the same "-" an unloaded one shows.
  const counts = summary.data?.summary;

  // `dayStart`/`dayEnd` are already the site's own day boundary (computed
  // above for the "Scheduled today" tile) - the agenda card's overdue/today/
  // upcoming split is otherwise the API's own business now, `now` included.
  const agenda = useApi<AgendaResponse>(
    can("appointment.read") && activeSiteId
      ? `/sites/${activeSiteId}/appointments/agenda?todayStart=${encodeURIComponent(
          dayStart
        )}&tomorrowStart=${encodeURIComponent(dayEnd)}&limit=5`
      : null
  );

  /**
   * Everything under "Compliance & performance", in one request.
   *
   * This used to be a seeded random-number generator in `lib/demo/`, drawing
   * plausible figures from the site id and the calendar day. The same two
   * parameters as the summary above, for the same reason: only the site's own
   * day boundary is ours to send, and every window behind it - a fortnight of
   * API's. One trailing fortnight covers the whole section, defined there rather
   * than twice in two languages.
   *
   * `visit.read` because a visit is the spine every figure here hangs off. The
   * sections a caller may not read come back null, so the page renders what
   * their capabilities cover rather than a 403.
   */
  const insightsRequest = useApi<SiteInsightsResponse>(
    can("visit.read") && activeSiteId
      ? `/sites/${activeSiteId}/insights?todayStart=${encodeURIComponent(
          dayStart
        )}&tomorrowStart=${encodeURIComponent(dayEnd)}`
      : null
  );

  const insights = insightsRequest.data?.insights ?? null;

  /**
   * Capability and scope only. **Nothing here about whether there is data.**
   *
   * This briefly gated on `hasVisitHistory`, so a workspace created that morning
   * would not open on five empty charts. Wrong twice over: it hid the section on
   * a failed request too, which is how a 500 read as a missing feature rather
   * than a bug - and "nothing assessed in 28 days" is itself a compliance
   * finding. An empty chart saying "Nothing recorded in this window yet" is
   * information; a chart that is not on the page says nothing and cannot be
   * asked why.
   *
   * So the section is on screen for anyone who may read it, and the cards draw
   * their own empty states off `?? []`.
   */
  const showInsights = can("visit.read") && !!activeSiteId;

  // Five checks, newest first, off the check log itself - so it cannot disagree
  // with the Visits page about what happened this morning, and it is not derived
  // from the charts above it.
  const recent = useApi<{ assessments: AssessmentFeedItem[] }>(
    can("assessment.read") && activeSiteId ? `/sites/${activeSiteId}/assessments?pageSize=5` : null
  );
  const activity = React.useMemo(() => activityFrom(recent.data?.assessments ?? []), [recent.data]);

  if (!user) return null;

  const siteCount = sites.data?.pagination.total;
  const teamCount = counts?.team;
  const hasVisitHistory = counts?.hasVisitHistory;

  const needsSites = siteCount !== undefined && siteCount < 2;
  const needsTeam = teamCount !== undefined && teamCount !== null && teamCount < 2;

  const showNextSteps = hasVisitHistory !== true && (needsSites || needsTeam);

  const rows = agenda.data?.appointments ?? [];

  return (
    <div className="page overview">
      <PageHeader
        className="overview__header"
        title={`${greeting()}, ${firstName(user.full_name) ?? "there"}`}
        description={tenancy.homeSite?.name ? `Today at ${tenancy.homeSite.name}` : undefined}
      />

      <div className="stat-grid">
        {can("appointment.read") && (
          <Stat
            label="Unassigned"
            icon={<UserX />}
            loading={summary.isLoading}
            value={counts?.unassigned ?? "-"}
          />
        )}
        {can("appointment.read") && (
          <Stat
            label="Scheduled today"
            icon={<CalendarDays />}
            loading={summary.isLoading}
            value={counts?.scheduledToday ?? "-"}
          />
        )}
        {can("visit.read") && (
          <Stat
            label="Visits today"
            icon={<RouteIcon />}
            loading={summary.isLoading}
            value={counts?.visitsToday ?? "-"}
          />
        )}
        {can("visit.read") && (
          <Stat
            label="Visits this week"
            icon={<CalendarRange />}
            loading={summary.isLoading}
            value={counts?.visitsThisWeek ?? "-"}
          />
        )}
      </div>

      {can("appointment.read") && (
        <Card className="agenda-brief">
          <CardHeader
            title="Agenda"
            actions={<TextLink to="/app/schedule?view=agenda">View all</TextLink>}
          />

          {/* Same rows as the schedule's agenda, assignee column included: the
              reader of this page is dispatching other people's work, so "who has
              it" is the column they are here for, and `Unassigned` on a row is
              the one thing on this card that needs acting on today.

              No tabs any more - the API already returns overdue first, then
              today, then upcoming, capped at five, so this just renders what
              comes back. */}
          <AgendaList
            appointments={rows}
            timezone={timezone}
            loading={agenda.isLoading}
            onOpen={(appointment, element, point) =>
              setDialog({ open: true, appointment, anchor: element, point: point ?? null })
            }
            surface="card"
            emptyTitle={AGENDA_EMPTY_TITLE}
            emptyDescription=""
          />

          {rows.length > 0 && (
            <CardFooter>
              <TextLink to="/app/schedule?view=agenda">View more</TextLink>
            </CardFooter>
          )}
        </Card>
      )}

      {showInsights && (
        <section className="insights" aria-labelledby="insights-heading">
          <div className="insights__head">
            <div className="insights__head-text">
              <h2 id="insights-heading" className="insights__title">
                Compliance &amp; performance
              </h2>
              <p className="insights__blurb">
                Whether the pre-task process ran, and what it found, across this site in the last 14
                days.
              </p>
            </div>
          </div>

          {/* Only when it happens, and never dismissible. A chart computed from
              a prefix of its window is a chart that understates, and a reader
              who is not told that is a reader acting on a number that is wrong
              in a direction they cannot guess. */}
          {insights?.truncated && (
            <p className="insights__note">
              This site records more work than one page of figures covers. These charts are computed
              from part of the window and understate it.
            </p>
          )}

          {/* In the section rather than instead of it. A failed aggregate is a
              broken request, not an absent feature, and the reader needs the
              `requestId` to be able to report it as one. */}
          {insightsRequest.error != null && (
            <ErrorState
              error={insightsRequest.error}
              onRetry={() => void insightsRequest.mutate()}
            />
          )}

          {insights?.meters && (
            <div className="meter-grid">
              {insights.meters.map((meter) => (
                <MeterStat
                  key={meter.key}
                  label={meter.label}
                  value={meter.value}
                  previous={meter.previous}
                  target={meter.target}
                  unit={meter.unit}
                  goal={meter.goal}
                  neutral={meter.key === "stop-work" || meter.key === "assessment-time"}
                  note={meter.note}
                />
              ))}
            </div>
          )}

          <div className="chart-grid">
            <Card className="chart-grid__wide">
              <CardHeader title="Assessment volume" subtitle="Scheduled against assessed" />
              <CardBody>
                {/* Two series, not three. `volume.completed` is still on the
                    wire and deliberately not drawn: a completed visit and an
                    assessed one are the same visit on all but a handful of
                    records, so the line sat on top of Assessed and the card
                    spent a third of its legend on a series a reader could not
                    separate from its neighbour. The gap that matters here is
                    scheduled against assessed - work planned against work with
                    a hazard record behind it - and the coverage meter above
                    already carries assessed-against-completed as a figure. */}
                <TrendChart
                  points={(insights?.volume ?? []).map((point) => ({
                    key: point.start,
                    label: formatDayAxisIn(point.start, timezone),
                    values: {
                      scheduled: point.scheduled,
                      assessed: point.assessed,
                    },
                  }))}
                  series={[
                    { key: "scheduled", label: "Scheduled", tone: "advisory", kind: "area" },
                    { key: "assessed", label: "Assessed", tone: "accent", kind: "line" },
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Process compliance"
                subtitle="Did the assessment happen, and did the PPE step get performed"
              />
              <CardBody>
                <TrendChart
                  points={(insights?.compliance ?? []).map((day) => ({
                    key: day.start,
                    label: formatDayAxisIn(day.start, timezone),
                    // Nulls stay nulls all the way to the path. A day with no
                    // visits has no compliance rate - there is no denominator -
                    // so `?? 0` here would draw a quiet Sunday as a total
                    // collapse. An absent day gets no vertex at all and the
                    // line runs straight past it: see `linePath`. What carries
                    // the absence is the meters above, which render `-` on an
                    // empty window and never a zero, and `sample` on each of
                    // them publishes the denominator the line cannot.
                    values: {
                      coverage: day.coverage,
                      ppeCompliance: day.ppeCompliance,
                    },
                  }))}
                  // Both rates run in the high nineties on a healthy site, so
                  // they overlap for most of the window, and they were teal
                  // `accent` against green `ok` - two hues that are hard to
                  // separate side by side and impossible where the lines cross.
                  //
                  // PPE moves to `cat-1`, violet. Not another semantic tone:
                  // `ok`, `caution` and `stop` mean something in this product,
                  // and a rate that is neither good nor bad by itself should
                  // not borrow one. `cat-*` is the categorical family and this
                  // is exactly its job - two series of the same kind that have
                  // to be told apart. Hue does that at any size, where a dash
                  // pattern at 2px is a smudge, so both series stay solid.
                  //
                  // Coverage keeps the area, because it is the one figure on
                  // this card a supervisor is accountable for.
                  series={[
                    {
                      key: "coverage",
                      label: "Assessment coverage",
                      tone: "accent",
                      kind: "area",
                    },
                    {
                      key: "ppeCompliance",
                      label: "PPE check compliance",
                      tone: "cat-1",
                      kind: "line",
                    },
                  ]}
                  unit="%"
                  axisMax={100}
                />
              </CardBody>
            </Card>

            {/* Its own chart rather than a third line above. Stop-work runs at a
                few percent against two series in the high nineties, and on a
                shared 0-100% axis it is a flat line along the floor - a series
                nobody can read is worse than one that was never plotted. */}
            <Card>
              <CardHeader
                title="Stop-work rate"
                subtitle="Share of analysed checks where a hazard halted the work"
              />
              <CardBody>
                <TrendChart
                  points={(insights?.compliance ?? []).map((day) => ({
                    key: day.start,
                    label: formatDayAxisIn(day.start, timezone),
                    // Null on a day with nothing analysed, as on the card
                    // above and for the same reason - see the note there.
                    values: { stopWorkRate: day.stopWorkRate },
                  }))}
                  series={[
                    { key: "stopWorkRate", label: "Stop-work rate", tone: "stop", kind: "area" },
                  ]}
                  unit="%"
                />
              </CardBody>
            </Card>

            {/* `chart-grid__donut`: the donut's content is short next to the
                taller charts sharing its grid row, and the grid stretches every
                card to the row's height by default. Without it the ring sits
                pinned to the top with the difference as dead space underneath -
                this makes the card a flex column instead, so the body can center
                the ring in whatever height the row gives it. */}
            <Card className="chart-grid__donut">
              <CardHeader
                title="Work mix by type"
                subtitle={`Against the location's configured work types`}
              />
              <CardBody>
                <DonutChart
                  slices={(insights?.types ?? []).map((slice) => ({
                    key: slice.code ?? "other",
                    label: slice.name,
                    value: slice.count,
                    // Work the catalogue does not name gets slate, which is
                    // also what `visitTypeColor` gives an unrecognised code.
                    tone: slice.code ? visitTypeColor(slice.code) : "cat-5",
                  }))}
                  centerLabel="Visits"
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Hazard findings by severity"
                subtitle="Advisory, caution, or stop issued"
              />
              <CardBody>
                <ColumnChart
                  data={(insights?.severity ?? []).map((day) => ({
                    key: day.start,
                    label: formatDayAxisIn(day.start, timezone),
                    values: day.counts,
                  }))}
                  // Bottom-up, so the most serious tier is the top segment.
                  series={SEVERITY_TIERS.map((tier) => ({
                    key: tier,
                    label: SEVERITY_META[tier].label,
                    tone: SEVERITY_META[tier].token,
                  }))}
                  totalNoun="findings"
                />
              </CardBody>
            </Card>

            <Card className="chart-grid__wide">
              <CardHeader
                title="By technician"
                subtitle="Everything above says whether the location is compliant; this says who to talk to"
              />
              <CardBody>
                <Table
                  minWidth="md"
                  head={
                    <>
                      <Th>Technician</Th>
                      <Th align="right">Visits</Th>
                      <Th>Assessment coverage</Th>
                      <Th hideBelow="md">PPE check compliance</Th>
                      <Th align="right" hideBelow="sm">
                        Median assessment
                      </Th>
                      <Th align="right" hideBelow="md">
                        Open findings
                      </Th>
                      <Th align="right">Stop-works</Th>
                    </>
                  }
                >
                  {/* An empty table is a header with nothing under it, which
                      reads as a component that failed rather than a site where
                      nobody has run a visit this month. */}
                  {(insights?.technicians ?? []).length === 0 && (
                    <Tr>
                      <TdSpan colSpan={7}>
                        <span className="chart-empty">Nothing recorded in this window yet.</span>
                      </TdSpan>
                    </Tr>
                  )}
                  {(insights?.technicians ?? []).map((row) => (
                    <Tr key={row.userId}>
                      <Td>
                        <span className="scorecard__who">
                          <PersonAvatar
                            person={{ id: row.userId, full_name: row.name, avatar_url: row.avatar }}
                            size="sm"
                          />
                          <span className="scorecard__name">{row.name}</span>
                        </span>
                      </Td>
                      <Td align="right">
                        <span className="tnum">{row.visits}</span>
                      </Td>
                      <Td>
                        <Rate value={row.coverage} />
                      </Td>
                      <Td hideBelow="md">
                        <Rate value={row.ppeCompliance} tone="ok" />
                      </Td>
                      <Td align="right" hideBelow="sm">
                        <span className="tnum">
                          {row.assessmentSeconds === null ? "-" : `${row.assessmentSeconds}s`}
                        </span>
                      </Td>
                      <Td align="right" hideBelow="md">
                        <span className="tnum">{row.openFindings}</span>
                      </Td>
                      <Td align="right">
                        {row.stopWorks > 0 ? (
                          <Badge tone="stop" size="sm">
                            {row.stopWorks}
                          </Badge>
                        ) : (
                          <span className="muted tnum">0</span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Table>
              </CardBody>
            </Card>
          </div>
        </section>
      )}

      {/* Outside the insights block on purpose. Those charts need a window of
          history to say anything; this is the check log and is true from the
          first visit, so gating it behind them would hide it on exactly the
          sites that have only just started recording. */}
      {activity.length > 0 && (
        <Card>
          <CardHeader
            title="Recent activity"
            actions={<TextLink to="/app/visits">View the visit log</TextLink>}
          />
          <CardBody>
            <ActivityFeed rows={activity} />
          </CardBody>
        </Card>
      )}

      {showNextSteps && (can("site.write") || can("user.invite")) && (
        <Card>
          <CardHeader title="Set up your workspace" subtitle="Two things worth doing first" />
          <CardBody className="overview-steps">
            {can("site.write") && (
              <NextStep
                to="/app/sites"
                icon={<MapPin />}
                title="Add your locations"
                body="A depot, region or client. Every assessment is filed against one."
              />
            )}
            {can("user.invite") && (
              <NextStep
                to="/app/team"
                icon={<UserPlus />}
                title="Invite your crew"
                body="They set their own password from the link and are working the same day."
              />
            )}
          </CardBody>
        </Card>
      )}

      {activeSiteId && (
        <AppointmentPopover
          open={dialog.open}
          anchor={dialog.anchor}
          point={dialog.point}
          siteId={activeSiteId}
          companyId={activeCompanyId}
          timezone={timezone}
          zoneLabel={zoneLabel}
          appointment={dialog.appointment}
          canWrite={can("appointment.write")}
          onClose={closeDialog}
          onSaved={() => {
            closeDialog();
            // The tiles above count the same rows. Marking one completed from
            // here writes a visit, so revalidating only the list leaves two
            // numbers on the same screen disagreeing with it.
            void agenda.mutate();
            void summary.mutate();
          }}
        />
      )}
    </div>
  );
}

/** Copy is identical on the phone's Agenda card - see `mobile-app/CLAUDE.md`'s
 *  parity rule. */
const AGENDA_EMPTY_TITLE = "Nothing on the agenda";

/**
 * A rate in the scorecard, with its bar.
 *
 * Null is "this person recorded nothing to compute it from" - a technician whose
 * visits all carried a PPE check has a figure and one whose visits carried none
 * has no figure, which is not the same as 0%. The bar goes with it: an empty
 * track reads as zero, and there is no zero here to read.
 */
function Rate({ value, tone }: { value: number | null; tone?: "ok" }) {
  if (value === null) return <span className="muted tnum">-</span>;

  return (
    <span className="scorecard__rate">
      <MiniBar value={value} tone={tone} />
      <span className="tnum">{value}%</span>
    </span>
  );
}

function NextStep({
  to,
  icon,
  title,
  body,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Link to={to} className="next-step">
      <IconTile className="next-step__tile">{icon}</IconTile>
      <span className="next-step__text">
        <span className="next-step__title">{title}</span>
        <span className="next-step__body">{body}</span>
      </span>
      <ArrowRight className="next-step__arrow" />
    </Link>
  );
}

/**
 * The activity feed.
 *
 * ── An ordered list, not a stack of divs ───────────────────────────────────
 *
 * These rows are a sequence in time and nothing else on the page is. `<ol>` is
 * what tells a screen reader that, and it is why the rail on the left is
 * decoration the markup does not depend on.
 *
 * ── The tone comes from the kind, never from the text ──────────────────────
 *
 * Severity means one thing in this product, so a stop-work row is `stop` and a
 * completed visit is `ok`, and the icon and the verb carry the same information
 * for anybody who cannot separate the two hues. Roughly 8% of this workforce
 * cannot.
 */
function ActivityFeed({ rows }: { rows: Activity[] }) {
  return (
    <ol className="activity">
      {rows.map((row) => {
        const { verb, tone, Icon } = ACTIVITY_META[row.kind];

        return (
          <li key={row.id} className="activity__row">
            <IconTile tone={tone} size="sm" className="activity__tile">
              <Icon />
            </IconTile>

            <div className="activity__text">
              <p className="activity__head">
                <span className="activity__verb">{verb}</span>
                <time className="activity__when" dateTime={row.at}>
                  {formatRelative(row.at)}
                </time>
              </p>
              <p className="activity__title">{row.title}</p>
              <p className="activity__detail">
                {row.detail}
                <span className="activity__who">
                  <PersonAvatar
                    person={{
                      id: row.technician.id,
                      full_name: row.technician.name,
                      avatar_url: row.technician.avatar,
                    }}
                    size="sm"
                  />
                  {row.technician.name}
                </span>
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const ACTIVITY_META: Record<
  ActivityKind,
  { verb: string; tone: "ok" | "stop" | "accent" | "caution"; Icon: typeof Check }
> = {
  "stop-work": { verb: "Stop-work raised", tone: "stop", Icon: Hand },
  failed: { verb: "Check failed", tone: "stop", Icon: Ban },
  attention: { verb: "Needs attention", tone: "caution", Icon: TriangleAlert },
  passed: { verb: "Check passed", tone: "ok", Icon: Check },
  running: { verb: "Check running", tone: "accent", Icon: ScanSearch },
  // Not a failure and the word matters: a module that did not apply to this job
  // is a different fact from one that found a problem.
  skipped: { verb: "Check skipped", tone: "caution", Icon: CircleSlash },
};

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function firstName(fullName: string | null): string | null {
  return fullName?.trim().split(/\s+/)[0] ?? null;
}
